import { DomNodes } from "../../constants"
import {
  // getFeedbackItems,
  getInitialCountingVariants,
  useAssessmentItemResponseScoring,
  useInteractionDivide,
  useItemResponse
} from "../../helpers"
import { FC, useCallback, useMemo, useEffect, useRef } from "react"
import { MatchTableComponent } from "../../ui/tables"
import { useMatchStatesControl } from "./hooks"
import { useSnapshot } from "valtio"
import { CurrentTestState } from "../../storage"
import {
  CheckCorrect,
  InteractionType,
  onSaveStudentAnswer,
  isQuestionComplete,
  ContainerType,
  NeutralSetting
} from "../../types"
import { ConstraintsAlertContainer } from "../../ui/containers/ConstraintsAlertContainer"

interface MatchInteractionComponentProps {
  itemId: string
  responseidentifier: string
  shuffle: string
  maxassociations: string
  minassociations: string
  children: JSX.Element[]
  accessibilityAttr?: { [key: string]: string }
  onSaveStudentAnswer?: onSaveStudentAnswer
  checkCorrect?: CheckCorrect
  isCompletedQuestion?: isQuestionComplete
  containerType: ContainerType
  isNeutralSetting?: NeutralSetting
}

export const MatchInteractionComponent: FC<
  MatchInteractionComponentProps
> = props => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null)
  const {
    itemId,
    responseidentifier: responseIdentifier,
    minassociations: minAssociations = "0",
    maxassociations: maxAssociations,
    children,
    accessibilityAttr,
    onSaveStudentAnswer,
    checkCorrect,
    isCompletedQuestion,
    containerType,
    isNeutralSetting
  } = props

  const { questionCompleted, correctAnswers } = isCompletedQuestion?.() ?? {
    questionCompleted: false,
    correctAnswers: []
  }

  const isLesson = containerType !== "assessment"
  const { currentItemResponse } = useSnapshot(CurrentTestState)
  const response = useItemResponse(currentItemResponse?.id, responseIdentifier)
  const { scoring } = useAssessmentItemResponseScoring(
    currentItemResponse?.itemId,
    responseIdentifier
  )

  const [[simpleMatchRows, simpleMatchColumns], prompt] = useInteractionDivide(
    DomNodes.simpleMatchSet,
    DomNodes.prompt,
    children
  )

  const rows = useMemo(
    () =>
      simpleMatchRows?.props?.children?.filter(
        (el: JSX.Element | string) => typeof el !== "string"
      ),
    [simpleMatchRows]
  )
  const columns = useMemo(
    () =>
      simpleMatchColumns?.props?.children?.filter(
        (el: JSX.Element | string) => typeof el !== "string"
      ),
    [simpleMatchColumns]
  )

  // Prevent error when column or rows are blank
  const hasMatchData = !!rows && !!columns

  const initialCountingVariants = useMemo(
    () =>
      hasMatchData
        ? getInitialCountingVariants([...rows, ...columns], response)
        : {},
    [rows, columns, response, hasMatchData]
  )

  const [changeMatch, isChecked, isDisabled, isFinished, selectMatches] =
    useMatchStatesControl(
      itemId,
      responseIdentifier,
      initialCountingVariants,
      Number(maxAssociations),
      Number(minAssociations),
      questionCompleted,
      correctAnswers
    )

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
      }
    }
  }, [])

  const isCorrect = useCallback(
    (matchId: string) => {
      if (isLesson && !!checkCorrect) {
        return checkCorrect(matchId)
      }

      return scoring?.response?.includes(matchId) ?? false
    },
    [scoring, checkCorrect, isLesson]
  )

  const handleMatchChange = useCallback(
    (id: string, checked: boolean) => {
      if (!isLesson) return

      // Clear any existing timeout
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }

      // Only save answer if checking (not unchecking)
      if (checked) {
        const isAnswerCorrect = isCorrect(id)

        onSaveStudentAnswer?.({
          interactionType: InteractionType.match,
          answerId: id,
          minChoices: Number(minAssociations),
          maxChoices: Number(maxAssociations),
          // Pass callback to unselect incorrect answer
          // This will be called either when feedback dialog closes (if feedback exists)
          // or after a delay (if no feedback exists)
          onFeedbackClose: !isAnswerCorrect
            ? () => {
                changeMatch(id, false)
              }
            : undefined
        } as any)
      }
    },
    [
      isCorrect,
      onSaveStudentAnswer,
      minAssociations,
      maxAssociations,
      changeMatch,
      isLesson
    ]
  )

  // const getColumnFeedbackItems = (columnId: string) =>
  //   getFeedbackItems(rows, groupStatistics, correctResponse, (correctResponse, rowId) =>
  //     correctResponse?.includes(`${rowId} ${columnId}`),
  //   )
  return (
    <div {...accessibilityAttr}>
      {prompt}
      {/* Only render actual table when theres content */}
      {hasMatchData && (
        <>
          {containerType === "assessment" && (
            <ConstraintsAlertContainer
              minChoices={Number(minAssociations)}
              maxChoices={Number(maxAssociations)}
              selectedCount={selectMatches.length}
            />
          )}
          <MatchTableComponent
            rows={rows}
            columns={columns}
            // groupMode={groupMode}
            // getFeedbackItems={getColumnFeedbackItems}
            isChecked={isChecked}
            isDisabled={isDisabled}
            isCorrect={isCorrect}
            isFinished={isFinished}
            onChange={(id: string, checked: boolean) => {
              changeMatch(id, checked)
              handleMatchChange(id, checked)
            }}
            completed={questionCompleted}
            checkCorrect={checkCorrect}
            isLesson={isLesson}
            isNeutralSetting={isNeutralSetting}
          />
        </>
      )}
    </div>
  )
}
